import { useEffect, useCallback, useRef } from 'react';
import {
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { ottoTokens } from '@/theme/ottoTokens';

/** CSS selector for all focusable elements within the modal */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const useStyles = makeStyles({
  // Backdrop overlay - absolute to canvas container
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: ottoTokens.overlayBackdrop,
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
    transitionProperty: 'opacity, visibility',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveDecelerateMax,
  },
  backdropVisible: {
    opacity: 1,
    visibility: 'visible',
  },

  // Modal container - fills canvas with margin
  modal: {
    position: 'absolute',
    top: '24px',
    left: '24px',
    right: '24px',
    bottom: '24px',
    zIndex: 1001,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow64,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    opacity: 0,
    visibility: 'hidden',
    transform: 'scale(0.95)',
    transitionProperty: 'opacity, transform, visibility',
    transitionDuration: tokens.durationGentle,
    transitionTimingFunction: tokens.curveDecelerateMax,
  },
  modalVisible: {
    opacity: 1,
    visibility: 'visible',
    transform: 'scale(1)',
  },

  // Content area
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
});

interface NodeEditorModalProps {
  children: React.ReactNode;
}

/**
 * NodeEditorModal - The modal container for the node editor
 * Handles backdrop, animations, and responsive sizing
 */
export const NodeEditorModal = ({ children }: NodeEditorModalProps) => {
  const styles = useStyles();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isClosingRef = useRef(false);

  // Use individual selectors to avoid infinite re-render loops
  const isOpen = useNodeEditorStore((s) => s.isOpen);
  const closeEditor = useNodeEditorStore((s) => s.closeEditor);
  const connectionPickerOpen = useNodeEditorStore((s) => s.connectionPickerOpen);

  // Handle escape key and focus trap (Tab/Shift+Tab cycling)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (connectionPickerOpen) {
        return;
      }
      e.stopPropagation();
      closeEditor();
      return;
    }

    // Focus trap: keep Tab/Shift+Tab fully within the modal
    if (e.key === 'Tab' && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const focusableElements = Array.from(focusable);
      const activeIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
      const direction = e.shiftKey ? -1 : 1;
      const fallbackIndex = e.shiftKey ? focusableElements.length - 1 : 0;
      const nextIndex =
        activeIndex === -1
          ? fallbackIndex
          : (activeIndex + direction + focusableElements.length) % focusableElements.length;

      e.preventDefault();
      e.stopPropagation();
      focusableElements[nextIndex]?.focus();
    }
  }, [closeEditor, connectionPickerOpen]);

  // Handle click outside to close
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeEditor();
    }
  }, [closeEditor]);

  // Save and restore focus when modal opens/closes
  useEffect(() => {
    let rafId: number;
    if (isOpen) {
      isClosingRef.current = false;
      if (document.activeElement instanceof HTMLElement) {
        previousFocusRef.current = document.activeElement;
      }
      // Focus the first focusable element inside the modal, or the modal itself as fallback
      rafId = requestAnimationFrame(() => {
        if (modalRef.current) {
          const firstFocusable = modalRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
          (firstFocusable ?? modalRef.current).focus();
        }
      });
    } else {
      // Mark as closing so the focusin listener doesn't intercept focus restoration
      isClosingRef.current = true;
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }
    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  // Redirect any focus that lands outside the modal back inside
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusIn = (e: FocusEvent) => {
      if (isClosingRef.current) return;
      const target = e.target as HTMLElement;
      if (!modalRef.current) return;
      if (modalRef.current.contains(target)) return;
      // Allow focus in Fluent UI portal surfaces (dropdowns, menus, popovers)
      if (target.closest?.('[role="listbox"], [role="menu"], [role="dialog"], [role="alertdialog"], [role="tooltip"]')) return;
      // Allow focus in the chat sidebar (assistant panel lives outside the modal)
      if (target.closest?.('[data-chat-sidebar]')) return;
      e.stopPropagation();
      const firstFocusable = modalRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstFocusable ?? modalRef.current).focus();
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={mergeClasses(
          styles.backdrop,
          isOpen && styles.backdropVisible
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={mergeClasses(
          styles.modal,
          isOpen && styles.modalVisible
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Node configuration"
        aria-hidden={!isOpen}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </>
  );
};

NodeEditorModal.displayName = 'NodeEditorModal';
